import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TextoTraducido } from '../i18n';

/**
 * Contiene un fallo de render para que no se lleve puesta la aplicación entera.
 *
 * ── Por qué hace falta ──────────────────────────────────────────────────────────
 * Sin esto, cualquier excepción durante el render vacía el árbol: queda un `<div id="root">`
 * sin nada adentro. Ni cartel, ni mensaje, ni un estado a medias — una página en blanco y una
 * línea en la consola del navegador, que es el único lugar donde nadie mira.
 *
 * Pasó con un `useState` declarado después de un `return` temprano: React abortó con el error
 * #310 y la pantalla de Consumo quedó vacía. El defecto se arregló en un minuto; encontrarlo
 * llevó bastante más, porque la pantalla no decía absolutamente nada.
 *
 * ── Por qué envuelve la ruta y no la aplicación ─────────────────────────────────
 * Envolviendo todo, un fallo en Consumo también se llevaría el menú y no habría forma de ir a
 * otro lado sin escribir la URL. Envolviendo la ruta, lo que falla es una sección: el resto
 * del panel sigue navegable y se puede seguir trabajando.
 *
 * ── Por qué muestra el error y no un «algo salió mal» ───────────────────────────
 * El mensaje técnico es feo y es exactamente lo que hace falta para arreglarlo. Esconderlo
 * detrás de una disculpa obliga a abrir la consola para saber lo mismo — con el paso extra de
 * tener que sospechar que hay algo que mirar.
 *
 * Es una clase porque `componentDidCatch` no tiene equivalente en hooks. Es el único
 * componente de clase del panel, y el motivo es ése.
 */

type Props = {
  children: ReactNode;
  /**
   * Cambiar esto reinicia el límite.
   *
   * Se le pasa la ruta: sin eso, una vez que una sección falla el borde queda en estado de
   * error para siempre y navegar a otra pantalla no muestra nada, porque el mismo borde sigue
   * mostrando el fallo anterior.
   */
  resetKey?: string;
};

type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Se conserva en la consola además de mostrarse: la traza de componentes que arma React
    // dice en qué parte del árbol ocurrió, y eso no entra en un cartel.
    console.error('Fallo al renderizar una sección del panel', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="rounded-2xl border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-6"
      >
        <h2 className="text-lg font-semibold">
          <TextoTraducido clave="error.seccionFallo" />
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-[var(--color-ink-muted)]">
          <TextoTraducido clave="error.seccionDetalle" />
        </p>

        <pre className="mt-4 overflow-x-auto rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-3 text-xs whitespace-pre-wrap">
          {error.message}
        </pre>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="rounded-xl border border-[var(--color-border-subtle)] px-4 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)]"
          >
            <TextoTraducido clave="comun.reintentar" />
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-xl border border-[var(--color-border-subtle)] px-4 py-2 text-sm font-medium transition hover:border-[var(--color-border-strong)]"
          >
            <TextoTraducido clave="comun.recargar" />
          </button>
        </div>
      </div>
    );
  }
}
